import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { 
  ClipboardCheck, Search, Filter, Clock, AlertCircle,
  ChevronRight, Settings, Package, Wrench, DollarSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const IQCQueue = ({ user, onLogout }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);

  useEffect(() => {
    loadQueue();
    loadStats();
  }, [filter]);

  const loadQueue = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/qc-stage/iqc/queue`, {
        params: { status: filter },
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevices(response.data.devices || []);
    } catch (error) {
      console.error('Failed to load IQC queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/qc-stage/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const filteredDevices = devices.filter(d => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      d.did?.toLowerCase().includes(query) ||
      d.imei?.toLowerCase().includes(query) ||
      d.brand?.toLowerCase().includes(query) ||
      d.model?.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getGradeBadge = (grade) => {
    const styles = {
      Z1: 'bg-green-500',
      Z2: 'bg-green-400',
      Z3: 'bg-lime-400',
      Z4: 'bg-yellow-400',
      Z5: 'bg-orange-400',
      Z6: 'bg-orange-500',
      Z7: 'bg-red-500'
    };
    return styles[grade] || 'bg-gray-400';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: ZOBOX_BLUE }}>
              <ClipboardCheck className="inline-block mr-2 h-6 w-6" />
              IQC Queue
            </h1>
            <p className="text-gray-600 mt-1">Internal Quality Check at TRC</p>
          </div>
          <a
            href="/trc/iqc-config"
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <Settings className="h-4 w-4" />
            IQC Configuration
          </a>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.iqc?.pending || 0}</div>
                <div className="text-sm text-gray-500">Pending IQC</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <ClipboardCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.iqc?.completed_today || 0}</div>
                <div className="text-sm text-gray-500">Completed Today</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Wrench className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.pna?.pending_approval || 0}</div>
                <div className="text-sm text-gray-500">PNA Pending</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats?.fqc?.pending || 0}</div>
                <div className="text-sm text-gray-500">FQC Pending</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by DID, IMEI, Brand, Model..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                data-testid="iqc-search-input"
              />
            </div>
            <div className="flex gap-2">
              {['pending', 'in_progress', 'completed'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    filter === status
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={filter === status ? { backgroundColor: ZOBOX_BLUE } : {}}
                >
                  {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Device List */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredDevices.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No devices in {filter} queue</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IMEI</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PQC Grade</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GRN Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredDevices.map(device => (
                  <tr key={device._id || device.did} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium">{device.did}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{device.brand} {device.model}</div>
                      <div className="text-xs text-gray-500">{device.variant || device.storage}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm">{device.imei}</span>
                    </td>
                    <td className="px-4 py-3">
                      {device.pqc_grade && (
                        <span className={`px-2 py-1 rounded text-white text-xs font-bold ${getGradeBadge(device.pqc_grade)}`}>
                          {device.pqc_grade}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(device.iqc_status)}`}>
                        {device.iqc_status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {device.grn_date ? new Date(device.grn_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={`/trc/iqc/${device.did}`}
                        className="flex items-center gap-1 text-sm font-medium hover:underline"
                        style={{ color: ZOBOX_BLUE }}
                        data-testid={`start-iqc-${device.did}`}
                      >
                        {device.iqc_status === 'pending' ? 'Start IQC' : 'View'}
                        <ChevronRight className="h-4 w-4" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Device Count */}
        <div className="mt-4 text-sm text-gray-500 text-right">
          Showing {filteredDevices.length} device(s)
        </div>
      </div>
    </Layout>
  );
};

export default IQCQueue;
